import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  Inject,
} from '@angular/core';
import { first, map } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { Store } from '@ngrx/store';
import { v4 as uuidv4 } from 'uuid';
import { forEachRight } from 'lodash';
import { AppState } from '../../../store';
import {
  selectConnectionSchema,
  selectConnectionSchemaData,
  selectConnectionSchemaErrors,
  selectConnectionsIsLoadingSchemaFlag,
} from '../../../connections/store/connections.selectors';
import { AnyComponentData, ConnectionSchemaField } from '../../package.models';
import {
  DEFAULT_SCHEMA_SETTINGS,
  getSettingsForComponent,
  SchemaImporterSettings,
} from '../../helpers/schema-importer-settings.helper';
import { ComponentTypeItem } from '../../../constants/component_types';
import { getConnectionSchema } from '../../../connections/store/connections.actions';
import { getDataForSchemaImporter } from '../../helpers/schema.helpers';

function createField(field: Partial<ConnectionSchemaField>): ConnectionSchemaField {
  return {
    alias: field.alias,
    data_type: field.data_type,
    name: field.name,
    disableSortable: field.disableSortable,
    disableTypeEdit: field.disableTypeEdit,
    required: field.required,
    predefined: field.predefined,
    _category: field.category,
    editable: field.editable,
  };
}

@Component({
  selector: 'schema-importer',
  template: `
    <div class="columns-schema-container" [ngClass]="{ disabled: isDisabled }">
      <xp-loader *ngIf="isLoading$ | async"></xp-loader>
      <div
        *ngIf="showReplaceErrorMessage && !(isLoading$ | async)"
        [ngClass]="{ 'show-notify': showReplaceErrorMessage }"
        class="mat-snack-bar-container errors-notify alert notify-template warning no-shadow"
      >
        <div class="message-container" [ngStyle]="{ display: 'inline-block' }">
          <p class="message" *ngIf="showReplaceErrorMessage">
            Detected source schema doesn't match selected schema. <a (click)="replaceFields()">Click here</a> to replace
            the selected fields with new schema.
          </p>
        </div>
      </div>
      <div *ngIf="!(isLoading$ | async)" class="columns-schema">
        <div class="columns-schema-items-container">
          <div class="columns-schema-item">
            <div class="columns-schema-header">
              <div class="title">
                Available fields
                <span class="items-count badge">{{
                  (connectionSchema$ | async).length + predefinedFields.length
                }}</span>
              </div>
              <button
                class="btn btn-default"
                (click)="selectAll()"
                *ngIf="availableFieldsOptions.allowSelectAllAvailableFields"
                [disabled]="fields.length === (connectionSchema$ | async).length + predefinedFields.length"
              >
                Select all
              </button>
            </div>
            <connection-schema
              [fields]="connectionSchema$ | async"
              (addField)="addField($event)"
              [settings]="availableFieldsOptions"
              [selectedFields]="fields"
              [predefinedFields]="predefinedFields"
              [isLoading]="isConnectionSchemaLoading"
              id="available-fields"
            ></connection-schema>
          </div>
          <div class="split-handler"></div>
          <div class="columns-schema-item">
            <div class="columns-schema-header">
              <div class="title">
                Selected fields <span class="items-count badge">{{ fields.length }}</span>
              </div>
              <button class="btn btn-default" (click)="removeAll()" [disabled]="fields.length === 0">Remove all</button>
            </div>
            <connection-schema
              [fields]="fieldsWithId"
              [settings]="selectedFieldsOptions"
              [hideTabs]="true"
              (addField)="addFieldBetween($event)"
              (addFieldToEnd)="addFieldToEnd()"
              (updateFields)="onUpdateFields($event)"
              (removeField)="removeField($event)"
              (updateValidation)="updateValidation.emit($event)"
              id="selected-fields"
            ></connection-schema>
          </div>
        </div>

        <div class="data-preview" *ngIf="component.hasDataPreview">
          <hr />
          <schema-importer-data-preview
            [data]="data$ | async"
            [component]="component"
            [rawComponent]="rawComponent"
            (refreshData)="refreshData()"
            [isPreviewLoading]="isLoading$ | async"
            [options]="selectedFieldsOptions"
            [dataPreviewDisabled]="dataPreviewDisabled"
          ></schema-importer-data-preview>
        </div>
      </div>
    </div>
  `,
})
export class SchemaImporterComponent implements OnInit, OnDestroy, OnChanges {
  @Input() fields: ConnectionSchemaField[] = [];
  @Input() isDisabled: boolean = false;
  @Input() dataPreviewDisabled: boolean = false;
  @Input() component: ComponentTypeItem;
  @Input() rawComponent: AnyComponentData;
  @Input() isConnectionSchemaLoading = false;
  @Output() fieldsChange = new EventEmitter<ConnectionSchemaField[]>();
  @Output() updateValidation = new EventEmitter<boolean>();
  @Output() refreshDataEvent = new EventEmitter();

  fieldsWithId: ConnectionSchemaField[] = [];

  error$ = this.store.select(selectConnectionSchemaErrors);
  errorTexts = [];
  isLoading$ = this.store.select(selectConnectionsIsLoadingSchemaFlag);
  data$ = this.store.select(selectConnectionSchemaData);
  connectionSchema$ = this.store
    .select(selectConnectionSchema)
    .pipe(map((fields) => fields.map((field) => ({ ...field, id: uuidv4() }))));
  errorsSubscriber: Subscription;
  connectionSchemaSubscriber: Subscription;
  selectedFieldsOptions: Partial<SchemaImporterSettings> = { ...DEFAULT_SCHEMA_SETTINGS };
  availableFieldsOptions: Partial<SchemaImporterSettings> = {
    ...DEFAULT_SCHEMA_SETTINGS,
    add: true,
    showSourceDesignatorInSelectedFields: true,
  };
  showReplaceErrorMessage = false;
  add_counter = 0;
  predefinedFields = [];

  constructor(private store: Store<AppState>, @Inject('$stateParams') private $stateParams: any) {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes.component?.currentValue) {
      const settings = getSettingsForComponent(this.component, this.rawComponent);

      this.selectedFieldsOptions = settings;
      this.availableFieldsOptions = settings;
      this.availableFieldsOptions.sourceDesginatorName = settings.sourceDesginatorName;
      // if you can't remove a specific field, you can't add specific field (all or nothing behavior)
      this.availableFieldsOptions.add = settings.allowToRemoveSelectedFields;
    }

    if (changes.fields?.currentValue) {
      this.fieldsWithId = this.fields.map((item) => ({ ...item, id: uuidv4() }));
      this.checkPresenceOfFields();
      this.setPredefinedFields();
    }
  }

  ngOnInit() {
    this.errorsSubscriber = this.error$.subscribe((error) => {
      if (!error) {
        this.errorTexts = [];
      } else {
        this.errorTexts = [error.message];
      }
    });
    this.fieldsWithId = this.fields.map((item) => ({ ...item, id: uuidv4() }));
    this.checkPresenceOfFields();
    this.setPredefinedFields();
  }

  addField({ field }) {
    this.fields = [...this.fields, field];
    this.fieldsChange.emit(this.fields);
  }

  createNewField(): ConnectionSchemaField {
    let fieldName = `FIELD_NAME_${this.add_counter}`;

    const getNextField = () => {
      const foundField = this.fields.find((item) => item.name === fieldName);
      if (foundField) {
        this.add_counter += 1;
        fieldName = `FIELD_NAME_${this.add_counter}`;
        getNextField();
      }
    };
    getNextField();

    const newField = {
      name: fieldName,
      alias: `FIELD_ALIAS_${this.add_counter}`,
      data_type: 'string',
      id: uuidv4(),
    };

    this.add_counter += 1;

    return newField;
  }

  addFieldToEnd() {
    const newField = this.createNewField();
    this.fields = [...this.fields, newField];
    this.fieldsChange.emit(this.fields);
  }

  addFieldBetween({ index }) {
    const newField = this.createNewField();
    if (index === 0) {
      this.fields = [newField, ...this.fields];
    } else if (index > 0) {
      this.fields = [...this.fields];
      this.fields.splice(index, 0, newField);
    } else {
      this.fields = [...this.fields, newField];
    }
    this.fieldsChange.emit(this.fields);
  }

  removeField(field: ConnectionSchemaField) {
    this.fields = this.fields.filter((item) => item.name !== field.name);
    this.fieldsChange.emit(this.fields);
  }

  onUpdateFields(fields: ConnectionSchemaField[]) {
    this.fieldsChange.emit(fields);
  }

  selectAll() {
    this.connectionSchema$.pipe(first()).subscribe((fields) => {
      this.fields = [...fields];
      this.fieldsChange.emit(this.fields);
    });
  }

  removeAll() {
    this.fields = [];
    this.fieldsChange.emit(this.fields);
  }

  checkPresenceOfFields() {
    if (this.connectionSchemaSubscriber) {
      this.connectionSchemaSubscriber.unsubscribe();
    }

    this.connectionSchemaSubscriber = this.connectionSchema$.subscribe((availableFields) => {
      const fields = availableFields;
      const missedFields = this.fields.filter(
        (field) => ![...fields, ...this.predefinedFields].find((availableField) => field.name === availableField.name),
      );
      if (missedFields.length) {
        this.showReplaceErrorMessage = true;
      } else {
        this.showReplaceErrorMessage = false;
      }
    });
  }

  removeError() {
    this.showReplaceErrorMessage = false;
  }

  replaceFields() {
    this.removeError();
    this.removeAll();
    this.selectAll();
  }

  refreshData() {
    this.store.dispatch(
      getConnectionSchema({
        onlyData: true,
        connectionId: this.rawComponent.connection?.id,
        connectionType: this.rawComponent.connection?.type,
        schemaRequestData: getDataForSchemaImporter(
          this.rawComponent,
          this.component.componentType,
          this.$stateParams.account_id,
        ),
      }),
    );

    this.refreshDataEvent.emit();
  }

  setPredefinedFields() {
    const fieldsToRemove = [];
    this.predefinedFields = [];

    // remove any existing predefined field that don't exist in predefined list
    this.fields.forEach((field) => {
      if (field.predefined) {
        const predefinedField = this.selectedFieldsOptions.preDefinedFields.find((item) => item.name === field.name);
        if (!predefinedField || field.markedToBeRemoved) {
          fieldsToRemove.push(field);
        }
      }
    });

    fieldsToRemove.forEach((field) => {
      this.removeField(field);
    });

    // possibly wrong place to store the predefined fields
    forEachRight(this.selectedFieldsOptions.preDefinedFields, (field) => {
      const existingFieldIndex = this.fields.findIndex((item) => item.name === field.name);
      if (existingFieldIndex > -1) {
        // eslint-disable-next-line no-param-reassign
        field = { ...field, ...this.fields[existingFieldIndex] };
        this.fields = [...this.fields];
        this.fields.splice(existingFieldIndex, 1, createField(field));
        this.predefinedFields = [...this.predefinedFields, field];
      } else {
        if (field.required) {
          this.fieldsChange.emit([createField(field), ...this.fields]);
        }
        this.predefinedFields = [...this.predefinedFields, field];
      }
    });
  }

  ngOnDestroy() {
    if (this.errorsSubscriber) {
      this.errorsSubscriber.unsubscribe();
    }

    if (this.connectionSchemaSubscriber) {
      this.connectionSchemaSubscriber.unsubscribe();
    }
  }
}
